import { useState, useEffect } from "react";
import { useParams } from "react-router";

import fetchAclymateApi from "@aclymatepackages/fetch-aclymate-api";

import { auth, useAccountData } from "../firebase";
import { extractScopeThreeEmissions } from "../components/scopeThreeEmissions";
import { isRunningInCodespaces } from "../detectCodespaces";
import { isObjectEmpty } from "../otherHelpers";

const INTERNAL_BASE_URL =
  process.env.REACT_APP_INTERNAL_FUNCTIONS_URL ||
  "https://us-central1-aclymate-internal.cloudfunctions.net";

export const fetchInternalApi = async ({
  endpoint,
  method = "POST",
  body,
  user,
  callback
}) => {
  const idToken = user ? await user.getIdToken() : null;
  const headers = {
    "Content-Type": "application/json",
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
  };
  const fetchOptions =
    method === "GET"
      ? { method, headers }
      : { method, headers, body: JSON.stringify(body || {}) };

  const response = await fetch(
    `${INTERNAL_BASE_URL}/${endpoint}`,
    fetchOptions
  );

  // A failing internal endpoint answers with a plain-text body ("Internal
  // Server Error"), not JSON, so parsing unconditionally replaced the real
  // failure with `SyntaxError: Unexpected token 'I'` (Sentry 7681371631) --
  // and when an error body did happen to parse, callers got an object where
  // they expected an array (`.filter is not a function`, Sentry 7701692539).
  // Reading the status first means the status reaches Sentry instead of the
  // shape of whatever the error page happened to contain.
  if (!response.ok) {
    const errorBody = await response.text().catch(() => "");
    throw new Error(
      `Internal API ${method} ${endpoint} failed (${response.status}): ${
        errorBody.slice(0, 300) || response.statusText
      }`
    );
  }

  const json = await response.json();
  return callback ? callback(json) : json;
};

export const fetchOurApi = async ({ user, accountId = "", ...otherProps }) => {
  const { currentUser } = auth || {};

  const lsAccountId = window.sessionStorage.getItem("accountId");

  const buildBaseFetchHost = () => {
    if (isRunningInCodespaces()) {
      // Use relative URL - will be proxied through React dev server to localhost:5001
      return "/aclymate-test-74e62/us-central1/app";
    }
    if (process.env.NODE_ENV === "development") {
      return "http://localhost:5001/aclymate-test-74e62/us-central1/app";
    }
    return process.env.REACT_APP_FIREBASE_FUNCTIONS_URL;
  };

  const usableUser = currentUser || user;
  const useableAccountId = lsAccountId || accountId;
  return await fetchAclymateApi({
    baseFetchHost: buildBaseFetchHost(),
    user: usableUser,
    accountId: useableAccountId,
    ...otherProps
  });
};

export const useApiData = ({
  path,
  method,
  data,
  isLoading,
  requiredFields
}) => {
  const { companyId } = useParams();

  const [apiData, setApiData] = useState(undefined);
  const [apiDataLoading, setApiDataLoading] = useState(true);

  useEffect(() => {
    const accountId = window.sessionStorage.getItem("accountId") || companyId;

    if (
      apiDataLoading &&
      !isLoading &&
      (!requiredFields ||
        requiredFields.every((field) => data && data[field] !== undefined))
    ) {
      fetchOurApi({
        accountId,
        path,
        method,
        data,
        callback: (response) => {
          setApiData(response);
          return setApiDataLoading(false);
        }
      });
    }
  }, [
    path,
    method,
    data,
    companyId,
    apiDataLoading,
    isLoading,
    requiredFields
  ]);

  return [apiData, apiDataLoading];
};

export const fetchPlaceId = async (description) =>
  await fetchOurApi({
    path: `/google-maps/place-id/${description}`,
    method: "GET",
    callback: ({ candidates }) => candidates[0]?.place_id
  });

export const fetchDirectionsMileage = async (to, from) => {
  const accountId = window.sessionStorage.getItem("accountId");

  return await fetchOurApi({
    accountId,
    path: "/calcs/cars/directions-mileage",
    method: "POST",
    data: { to, from },
    callback: ({ totalMileage }) => totalMileage
  });
};

export const fetchTotalMileageCarbon = async (
  { mileage, vehicle, address },
  callback = ({ tonsCo2e }) => tonsCo2e
) =>
  await fetchOurApi({
    path: "/calcs/cars/total-mileage-carbon",
    method: "POST",
    data: { mileage: Number(mileage), vehicle, address },
    callback
  });

export const findPropertyDetails = async ({
  description: locationDescription,
  city,
  state,
  zipCode
}) =>
  await fetchOurApi({
    path: "/integrations/realty-mole-property/property-details",
    method: "POST",
    data: {
      locationDescription,
      city,
      state,
      zipCode
    },
    callback: (res) => res
  });

export const fetchUserWithEmail = async (email) =>
  await fetchOurApi({
    path: "/onboarding/check-email",
    method: "POST",
    data: { email },
    callback: ({ success, data }) => {
      if (success) {
        return data;
      }
      return false;
    }
  });

export const sendSupportEmail = async ({
  name,
  email,
  companyName,
  subject,
  message,
  callback
}) =>
  await fetchOurApi({
    path: "/emails/support-inquiry",
    method: "POST",
    data: { name, email, companyName, subject, message },
    callback
  });

export const fetchAddressEGrid = async (address) =>
  await fetchOurApi({
    path: "/calcs/utilities/egrid-from-address",
    method: "POST",
    data: { address },
    callback: (response) => response?.eGrid
  });

export const fetchFuelBillCarbon = async ({
  billValue,
  billDate,
  gasStation
}) =>
  await fetchOurApi({
    path: "/calcs/cars/fuel-bill-carbon",
    method: "POST",
    data: {
      fuelBill: {
        billValue,
        billDate: billDate || new Date(),
        gasStation
      }
    },
    callback: ({ tonsCo2 }) => tonsCo2
  });

export const newAuthUser = async (
  { email, password, displayName, phoneNumber },
  callback = (res) => res
) =>
  await fetchOurApi({
    path: "/onboarding/new-auth-user",
    method: "POST",
    data: { email, password, displayName, phoneNumber },
    callback
  });

export const queryMatchingCompanies = async (name) =>
  await fetchOurApi({
    path: `/companies/query-matching-companies`,
    method: "POST",
    data: { name },
    callback: ({ matchingCompanies }) => matchingCompanies
  });

export const newVendorRelationship = async (data) => {
  return await fetchOurApi({
    accountId: data?.buyerId,
    path: "/companies/new-vendor-relationship",
    method: "POST",
    data,
    callback: (res) => res
  });
};

export const setUserCustomClaims = async ({
  uid,
  accountId,
  accountCreationType
}) =>
  await fetchOurApi({
    path: `/users/set-claims`,
    method: "POST",
    data: {
      uid,
      accountId,
      accountCreationType
    },
    callback: (res) => res
  });

export const updateAccountData = async (collection, accountId, updateObj) =>
  await fetchOurApi({
    path: "/onboarding/update-account-data",
    method: "POST",
    data: {
      accountId,
      collection,
      updateObj
    }
  });


export const fetchNearestAirportFromCoordinates = async (
  { lat, lng },
  callback = (res) => res
) =>
  await fetchOurApi({
    path: `/airports/nearest-airport/lat:${lat},lng:${lng}`,
    method: "GET",
    callback
  });

export const fetchPeriodTransactions = async ({ startDate, endDate }) => {
  const buyerId = window.sessionStorage.getItem("accountId");

  return await fetchOurApi({
    path: "/transactions/fetch-period-transactions",
    method: "POST",
    data: { startDate, endDate, buyerId },
    callback: ({ transactions = [] }) =>
      extractScopeThreeEmissions(transactions)
  });
};

export const fetchAddressesGcd = async (to, from) => {
  if (!isObjectEmpty(to) && !isObjectEmpty(from)) {
    return await fetchOurApi({
      path: "/calcs/travel/fetch-addresses-gcd",
      method: "POST",
      data: {
        to,
        from
      },
      callback: (res) => res
    });
  }
  return {};
};

export const useFetchProductReportSummary = () => {
  const [{ name: companyName }] = useAccountData();

  return (product, callback = ({ summary }) => summary) =>
    fetchOurApi({
      path: "/open-ai/fetch-product-report-summary",
      method: "POST",
      data: {
        product,
        companyName
      },
      callback
    });
};

export const fetchVendorCompanyData = async (vendorId, relationshipId) =>
  await fetchOurApi({
    path: "/vendors/fetch-vendor-company-data",
    method: "POST",
    data: { vendorId, relationshipId },
    callback: (res) => res
  });

export const requestProductBom = async ({ productId, relationshipId }) =>
  await fetchOurApi({
    path: "/vendors/request-product-bom",
    method: "POST",
    data: { productId, relationshipId },
    callback: (res) => res
  });

export const syncProductComponentAccess = async (productId) =>
  await fetchOurApi({
    path: "/products/sync-product-component-access",
    method: "POST",
    data: { productId },
    callback: (res) => res
  });

export const removeProductVendor = async ({ productId, lifecycleEventId }) =>
  await fetchOurApi({
    path: "/products/remove-product-vendor",
    method: "POST",
    data: { productId, lifecycleEventId },
    callback: (res) => res
  });

export const archiveProduct = async (productId) =>
  await fetchOurApi({
    path: "/products/archive-product",
    method: "POST",
    data: { productId },
    callback: (res) => res
  });

export const cancelProductBomRequest = async ({ productId, lifecycleEventId }) =>
  await fetchOurApi({
    path: "/products/cancel-product-bom-request",
    method: "POST",
    data: { productId, lifecycleEventId },
    callback: (res) => res
  });

export const reassignProductVendor = async ({
  productId,
  lifecycleEventId,
  lifecycleFields
}) =>
  await fetchOurApi({
    path: "/products/reassign-product-vendor",
    method: "POST",
    data: { productId, lifecycleEventId, lifecycleFields },
    callback: (res) => res
  });

export const shareProductBom = async ({ lifecycleEventId, vendorId, share }) =>
  await fetchOurApi({
    path: "/products/share-product-bom",
    method: "POST",
    data: { lifecycleEventId, vendorId, share },
    callback: (res) => res
  });

export const fetchNaicsCodes = (description) =>
  fetchOurApi({
    path: "/open-ai/fetch-naics-codes-from-description",
    method: "POST",
    data: { description },
    callback: ({ naicsCodes = [] }) => naicsCodes
  });
